import { z } from "zod";
import { isValidPhoneNumber } from "react-phone-number-input";

export type UpdateOrderSchema = z.infer<typeof updateOrderSchema>;
export const updateOrderSchema = z.object({
  // Sender details
  senderCustomerId: z.string().optional(),
  pickupDepotId: z.string().optional(),
  senderName: z.string().min(1, "Sender name is required"),
  senderPhone: z.string().min(1, "Sender phone is required"),
  pickupAddress: z.object({
    line1: z.string().min(1, "Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    province: z.string().min(1, "Province is required"),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  // Recipient details
  recipientName: z.string().min(1, "Recipient name is required"),
  recipientPhone: z.string().min(1, "Recipient phone is required"),
  deliveryDepotId: z.string().optional(),
  deliveryAddress: z.object({
    line1: z.string().min(1, "Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    province: z.string().min(1, "Province is required"),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  // Order type
  type: z.enum(["STANDARD", "EXPRESS", "SAME_DAY"]).optional(),
  parcels: z.array(
    z.object({
      weight: z.number().min(0),
      dimensions: z.object({
        length: z.number().min(0),
        width: z.number().min(0),
        height: z.number().min(0),
      }),
      isFragile: z.boolean(),
      description: z.string().optional(),
      requiresCooling: z.boolean().optional(),
      requiresWarm: z.boolean().optional(),
      isPriority: z.boolean().optional(),
      requireSignature: z.boolean().optional(),
      extraInsurance: z.boolean().optional(),
      leaveAtDoor: z.boolean().optional(),
      value: z.number().optional(),
    })
  ),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
});

export type ParcelCreateSchema = z.infer<typeof parcelCreateSchema>;
export const parcelCreateSchema = z.object({
  weight: z.number().min(0),
  dimensions: z.object({
    length: z.number().min(0),
    width: z.number().min(0),
    height: z.number().min(0),
  }),
  isFragile: z.boolean(),
  description: z.string().optional(),
});

export type CreateOrderSchema = z.infer<typeof createOrderSchema>;
export const createOrderSchema = z.object({
  senderCustomerId: z.string().optional(),
  senderCustomerNumber: z.string().optional(),
  recipientCustomerId: z.string().optional(),
  recipientCustomerNumber: z.string().optional(),
  type: z.enum(["STANDARD", "EXPRESS", "SCHEDULED", "SAME_DAY"]),
  status: z.enum([
    "PENDING",
    "PICKUP_ASSIGNED",
    "PICKED_UP",
    "AT_DEPOT",
    "SORTED",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "FAILED",
    "CANCELLED",
    "FIRST_ATTEMPT_FAILED",
    "SECOND_ATTEMPT_FAILED",
    "RETURNED",
  ]),
  recipientName: z.string().nonempty("Recipient name is required"),
  recipientEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  recipientPhone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  deliveryAddress: z.object({
    line1: z.string().nonempty("Delivery address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z.string().nonempty("Postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  senderName: z.string().nonempty("Sender name is required"),
  senderEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  senderPhone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  pickupAddress: z.object({
    line1: z.string().nonempty("Pickup address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z
      .string()
      .nonempty("Pickup postal code is required")
      .min(1, "Pickup postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  parcels: z.array(
    z.object({
      weight: z.coerce.number().optional(),
      dimensions: z
        .object({
          length: z.coerce.number(),
          width: z.coerce.number(),
          height: z.coerce.number(),
        })
        .optional(),
      isFragile: z.boolean().optional(),
      requiresCooling: z.boolean().optional(),
      requiresWarm: z.boolean().optional(),
      isPriority: z.boolean().optional(),
      requireSignature: z.boolean().optional(),
      extraInsurance: z.boolean().optional(),
      leaveAtDoor: z.boolean().optional(),
      value: z.coerce.number().optional(),
    })
  ),
  customerType: z.enum(["INDIVIDUAL", "BUSINESS"]).default("INDIVIDUAL"),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
});

export type UserProfileSchema = z.infer<typeof userProfileSchema>;
export const userProfileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  preferredName: z.string().optional(),
  email: z.string().email("Invalid email address"),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
});
